import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Drawer, Snackbar, Alert,
  CircularProgress, FormControl, InputLabel, Select, MenuItem, Grid,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { accountingPeriodService } from '../services/accountingPeriodService';

export default function AccountingPeriods() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFiscalYear, setFilterFiscalYear] = useState('');

  const [createDialog, setCreateDialog] = useState({
    open: false,
    form: { period_name: '', start_date: '', end_date: '', fiscal_year: '', period_number: '' },
    submitting: false
  });

  const [detailDrawer, setDetailDrawer] = useState({
    open: false,
    period: null,
    loading: false
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    periodId: null,
    action: '', // 'close' or 'reopen'
    reason: '',
    submitting: false
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterFiscalYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterStatus) filters.status = filterStatus;
      if (filterFiscalYear) filters.fiscal_year = filterFiscalYear;
      const data = await accountingPeriodService.getAll(filters);
      setPeriods(data ?? []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to load periods', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOpen = () => {
    setCreateDialog({
      open: true,
      form: { period_name: '', start_date: '', end_date: '', fiscal_year: '', period_number: '' },
      submitting: false
    });
  };

  const handleCreateSubmit = async () => {
    const { form } = createDialog;
    if (!form.period_name || !form.start_date || !form.end_date) {
      setSnackbar({ open: true, message: 'Period name, start date, and end date are required', severity: 'error' });
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      setSnackbar({ open: true, message: 'End date must be after start date', severity: 'error' });
      return;
    }

    setCreateDialog(d => ({ ...d, submitting: true }));
    try {
      const payload = {
        period_name: form.period_name,
        start_date: form.start_date,
        end_date: form.end_date,
        fiscal_year: form.fiscal_year || null,
        period_number: form.period_number ? parseInt(form.period_number, 10) : null
      };
      await accountingPeriodService.create(payload);
      setCreateDialog(d => ({ ...d, open: false }));
      setSnackbar({ open: true, message: 'Accounting period created successfully', severity: 'success' });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error?.message || err.message || 'Failed to create period', severity: 'error' });
    } finally {
      setCreateDialog(d => ({ ...d, submitting: false }));
    }
  };

  const handleView = async (id) => {
    setDetailDrawer({ open: true, period: null, loading: true });
    try {
      const data = await accountingPeriodService.getById(id);
      setDetailDrawer({ open: true, period: data, loading: false });
    } catch (err) {
      setDetailDrawer({ open: true, period: null, loading: false });
      setSnackbar({ open: true, message: err.message || 'Failed to load period details', severity: 'error' });
    }
  };

  const handleClosePeriod = (periodId) => {
    setConfirmDialog({
      open: true,
      periodId,
      action: 'close',
      reason: '',
      submitting: false
    });
  };

  const handleReopenPeriod = (periodId) => {
    setConfirmDialog({
      open: true,
      periodId,
      action: 'reopen',
      reason: '',
      submitting: false
    });
  };

  const handleConfirmAction = async () => {
    const { periodId, action, reason } = confirmDialog;
    if (action === 'reopen' && (!reason || reason.trim().length < 10)) {
      setSnackbar({ open: true, message: 'Reopening requires a justification of at least 10 characters', severity: 'error' });
      return;
    }

    setConfirmDialog(d => ({ ...d, submitting: true }));
    try {
      if (action === 'close') {
        await accountingPeriodService.close(periodId, { reasoning: reason || 'Period closed via UI' });
        setSnackbar({ open: true, message: 'Period closed successfully', severity: 'success' });
      } else {
        await accountingPeriodService.reopen(periodId, { reasoning: reason });
        setSnackbar({ open: true, message: 'Period reopened successfully', severity: 'success' });
      }
      setConfirmDialog({ open: false, periodId: null, action: '', reason: '', submitting: false });
      loadData();
      if (detailDrawer.open && detailDrawer.period?.period_id === periodId) {
        handleView(periodId);
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || err.message || `Failed to ${action} period`,
        severity: 'error'
      });
    } finally {
      setConfirmDialog(d => ({ ...d, submitting: false }));
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case 'open': return 'success';
      case 'closed': return 'warning';
      case 'locked': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString();
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayIcon color="primary" />
          <Typography variant="h5" component="h1">Accounting Periods</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}>
          Create Period
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 160 }} size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
            <MenuItem value="locked">Locked</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Fiscal Year"
          size="small"
          value={filterFiscalYear}
          onChange={(e) => setFilterFiscalYear(e.target.value)}
          sx={{ width: 140 }}
        />
      </Box>

      {/* Table */}
      <Paper elevation={1}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell>Period Name</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Fiscal Year</TableCell>
                <TableCell>Period #</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : periods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No accounting periods found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                periods.map((period) => (
                  <TableRow key={period.period_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {period.period_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(period.start_date)}</TableCell>
                    <TableCell>{formatDate(period.end_date)}</TableCell>
                    <TableCell>{period.fiscal_year || '—'}</TableCell>
                    <TableCell>{period.period_number ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={period.status}
                        size="small"
                        color={statusColor(period.status)}
                        sx={{ textTransform: 'capitalize', fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {period.status === 'open' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleClosePeriod(period.period_id)}
                            sx={{ minWidth: 0, px: 1.5, fontSize: '0.75rem' }}
                          >
                            Close
                          </Button>
                        )}
                        {period.status === 'closed' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<LockOpenIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleReopenPeriod(period.period_id)}
                            sx={{ minWidth: 0, px: 1.5, fontSize: '0.75rem' }}
                          >
                            Reopen
                          </Button>
                        )}
                        <IconButton size="small" onClick={() => handleView(period.period_id)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={createDialog.open} onClose={() => setCreateDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Create Accounting Period</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Period Name"
            margin="normal"
            value={createDialog.form.period_name}
            onChange={(e) => setCreateDialog(d => ({ ...d, form: { ...d.form, period_name: e.target.value } }))}
            required
          />
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={createDialog.form.start_date}
                onChange={(e) => setCreateDialog(d => ({ ...d, form: { ...d.form, start_date: e.target.value } }))}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={createDialog.form.end_date}
                onChange={(e) => setCreateDialog(d => ({ ...d, form: { ...d.form, end_date: e.target.value } }))}
                required
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Fiscal Year"
                margin="normal"
                value={createDialog.form.fiscal_year}
                onChange={(e) => setCreateDialog(d => ({ ...d, form: { ...d.form, fiscal_year: e.target.value } }))}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Period Number"
                type="number"
                margin="normal"
                value={createDialog.form.period_number}
                onChange={(e) => setCreateDialog(d => ({ ...d, form: { ...d.form, period_number: e.target.value } }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(d => ({ ...d, open: false }))} disabled={createDialog.submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={createDialog.submitting || !createDialog.form.period_name || !createDialog.form.start_date || !createDialog.form.end_date}
          >
            {createDialog.submitting ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog (Close/Reopen) */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textTransform: 'capitalize' }}>
          {confirmDialog.action} Period
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to {confirmDialog.action} this accounting period?
          </Typography>
          <TextField
            fullWidth
            label={confirmDialog.action === 'reopen' ? 'Justification (required, min 10 chars)' : 'Reason (optional)'}
            multiline
            rows={3}
            value={confirmDialog.reason}
            onChange={(e) => setConfirmDialog(d => ({ ...d, reason: e.target.value }))}
            required={confirmDialog.action === 'reopen'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(d => ({ ...d, open: false }))} disabled={confirmDialog.submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirmDialog.action === 'close' ? 'primary' : 'warning'}
            onClick={handleConfirmAction}
            disabled={confirmDialog.submitting || (confirmDialog.action === 'reopen' && confirmDialog.reason.trim().length < 10)}
          >
            {confirmDialog.submitting ? <CircularProgress size={20} /> : confirmDialog.action}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={detailDrawer.open}
        onClose={() => setDetailDrawer({ ...detailDrawer, open: false })}
        PaperProps={{ sx: { width: '45%', minWidth: 380, p: 3 } }}
      >
        {detailDrawer.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : detailDrawer.period ? (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">{detailDrawer.period.period_name}</Typography>
              <IconButton onClick={() => setDetailDrawer({ ...detailDrawer, open: false })}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Chip
              label={detailDrawer.period.status}
              color={statusColor(detailDrawer.period.status)}
              sx={{ textTransform: 'capitalize', mb: 2 }}
            />

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Start Date</Typography>
                <Typography variant="body1">{formatDate(detailDrawer.period.start_date)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">End Date</Typography>
                <Typography variant="body1">{formatDate(detailDrawer.period.end_date)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Fiscal Year</Typography>
                <Typography variant="body1">{detailDrawer.period.fiscal_year || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Period Number</Typography>
                <Typography variant="body1">{detailDrawer.period.period_number ?? '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body1">{formatDate(detailDrawer.period.created_at)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Updated</Typography>
                <Typography variant="body1">{formatDate(detailDrawer.period.updated_at)}</Typography>
              </Grid>
            </Grid>

            {detailDrawer.period.metadata && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Metadata</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(detailDrawer.period.metadata, null, 2)}
                  </Typography>
                </Paper>
              </>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
              {detailDrawer.period.status === 'open' && (
                <Button variant="outlined" onClick={() => handleClosePeriod(detailDrawer.period.period_id)}>
                  Close Period
                </Button>
              )}
              {detailDrawer.period.status === 'closed' && (
                <Button variant="outlined" color="warning" onClick={() => handleReopenPeriod(detailDrawer.period.period_id)}>
                  Reopen Period
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <Typography color="text.secondary">Period not found.</Typography>
        )}
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
